/**
 * workflow-layout.ts  (Milestone 4)
 *
 * Pure function that converts an IBuilderModel into a render tree (LayoutNode[]).
 *
 * The algorithm mirrors the POC's `chain()` / `branchRow()` logic:
 *   - Linear step: state with exactly one outgoing transition that has NO `.two`
 *     → emits [state, event, ...continue] in a single column.
 *   - Branch: state with MORE than one outgoing transition, OR one transition
 *     that has a `.two` → emits a `{kind:'branch', columns:[...]}` node.
 *   - Guarded transition: transition has `.guard` WITH clauses AND `.two`
 *     → emits `decision` then a branch with two columns (False→one, True→two).
 *   - Two outcomes without guard: branch with two columns (Outcome ①/②).
 *   - Revisited stateCode (tracked in a `seen` Set): emit `{kind:'join'}`.
 */

import { IBuilderModel, ITransition } from '../models/workflow-builder.model';

// ─── public types ────────────────────────────────────────────────────────────

export type LayoutNode =
  | {
      kind: 'state';
      code: string;
      ref: { tid: string; which: 'one' | 'two' } | null;
    }
  | { kind: 'event'; tid: string }
  | { kind: 'decision'; tid: string } // a guarded transition
  | { kind: 'branch'; columns: LayoutNode[][] } // horizontal fan-out
  | { kind: 'join'; code: string }; // revisited state (dedup / cycle)

// ─── implementation ──────────────────────────────────────────────────────────

/**
 * Build the full layout tree starting from state `'NEW'`.
 * Returns a flat LayoutNode[] representing a top-to-bottom column, where
 * branch nodes carry their children.
 */
export function buildLayout(model: IBuilderModel): LayoutNode[] {
  const seen = new Set<string>();
  return chain('NEW', null, seen, model);
}

// ─── internal helpers ────────────────────────────────────────────────────────

/**
 * Recursively build the layout column for `code`.
 * `inRef` – the endState reference (tid + which) that leads into this state,
 *           or null for the root 'NEW' state.
 */
function chain(
  code: string,
  inRef: { tid: string; which: 'one' | 'two' } | null,
  seen: Set<string>,
  model: IBuilderModel
): LayoutNode[] {
  const nodes: LayoutNode[] = [];
  let cur = code;
  let ref = inRef;

  for (;;) {
    // Cycle / convergence guard
    if (seen.has(cur)) {
      nodes.push({ kind: 'join', code: cur });
      return nodes;
    }
    seen.add(cur);

    // Emit the state node
    nodes.push({ kind: 'state', code: cur, ref });

    // Find all outgoing transitions from `cur`
    const outs = model.transitions.filter(t => t.startState === cur);

    if (outs.length === 0) {
      // Terminal state — no more nodes
      return nodes;
    }

    if (outs.length === 1 && !outs[0].two) {
      // Pure linear step → continue straight down in this column
      const t = outs[0];
      nodes.push({ kind: 'event', tid: t.id });
      ref = { tid: t.id, which: 'one' };
      cur = t.one.stateCode;
      continue;
    }

    // Branch point: emit the branch row and return
    nodes.push(branchRow(outs, seen, model));
    return nodes;
  }
}

/**
 * Build a branch node from a list of outgoing transitions.
 * Each transition becomes one column:
 *   – guarded (has .guard with clauses AND .two) → event, decision, false/true sub-branch
 *   – two outcomes no guard → event, outcome-①/② sub-branch
 *   – simple single-outcome → event, continue column
 */
function branchRow(
  outs: ITransition[],
  seen: Set<string>,
  model: IBuilderModel
): LayoutNode {
  const columns: LayoutNode[][] = outs.map(t => {
    const col: LayoutNode[] = [];

    const hasGuard = !!(t.guard && t.guard.clauses.length > 0 && t.two);
    const hasTwoNoGuard = !!(t.two && !(t.guard && t.guard.clauses.length > 0));

    col.push({ kind: 'event', tid: t.id });

    if (hasGuard) {
      // Emit a decision node, then a 2-column sub-branch: False→one, True→two
      col.push({ kind: 'decision', tid: t.id });

      // Need a snapshot of seen BEFORE recursing into false/true branches —
      // each branch is independent but shares the same `seen` for dedup.
      // The POC passes the SAME seen set, so if both branches share a state
      // the second one becomes a join.  We mirror that behaviour.
      const falseCol = chain(
        t.one.stateCode,
        { tid: t.id, which: 'one' },
        seen,
        model
      );
      const trueCol = chain(
        t.two!.stateCode,
        { tid: t.id, which: 'two' },
        seen,
        model
      );
      col.push({ kind: 'branch', columns: [falseCol, trueCol] });
      return col;
    }

    if (hasTwoNoGuard) {
      // Two outcomes without a guard
      const oneCol = chain(
        t.one.stateCode,
        { tid: t.id, which: 'one' },
        seen,
        model
      );
      const twoCol = chain(
        t.two!.stateCode,
        { tid: t.id, which: 'two' },
        seen,
        model
      );
      col.push({ kind: 'branch', columns: [oneCol, twoCol] });
      return col;
    }

    // Simple: single outcome, continue down
    const rest = chain(
      t.one.stateCode,
      { tid: t.id, which: 'one' },
      seen,
      model
    );
    col.push(...rest);
    return col;
  });

  return { kind: 'branch', columns };
}
